(() => {
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const dirty = new Map();
  const started = Date.now();
  let pending = 0, revision = 0, allowed = false, failed = false, timer, queue = Promise.resolve();
  const controls = () => {
    const blocked = !allowed || pending > 0 || dirty.size > 0;
    document.querySelectorAll('.cart__ctas, .cart__dynamic-checkout-buttons').forEach(element => {
      element.inert = blocked;
      element.setAttribute('aria-busy', String(blocked));
    });
    document.querySelectorAll('.cart__checkout-button').forEach(button => {
      button.disabled = blocked || document.querySelector('cart-items')?.classList.contains('is-empty');
    });
  };
  const close = () => { allowed = false; revision++; controls(); };
  const show = error => {
    const box = document.getElementById('cart-errors');
    const messages = window.filameltPropertyMessages;
    if (!box || !messages) return;
    box.replaceChildren();
    box.setAttribute('role', error ? 'alert' : 'status');
    if (allowed) return;
    box.textContent = error ? messages.error + ' ' : messages.busy;
    if (error) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = messages.retry;
      retry.addEventListener('click', () => location.reload());
      box.append(retry);
    }
  };
  const read = async () => {
    const response = await nativeFetch(`${root}cart.js`, {cache:'no-store', headers:{Accept:'application/json'}});
    if (!response.ok) throw new Error('CART_READ_FAILED');
    return response.json();
  };
  const itemIdentity = item => JSON.stringify([item.variant_id, Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b))]);
  const feeQuantity = (properties, quantity) => {
    if (Object.keys(properties).some(key => /^_gpo_quantity_/.test(key) && !['_gpo_quantity_fixed','_gpo_quantity_dynamic','_gpo_quantity_mix'].includes(key))) throw new Error('UNKNOWN_FEE_RULE');
    const rules = ['_gpo_one_time_charge', '_gpo_quantity_fixed', '_gpo_quantity_dynamic', '_gpo_quantity_mix', '_gpo_per_character'].filter(key => Object.hasOwn(properties, key));
    if (rules.length > 1) throw new Error('AMBIGUOUS_FEE_RULE');
    if (!quantity) return 0;
    if (!rules.length) return quantity;
    const key = rules[0];
    if (key === '_gpo_one_time_charge') return 1;
    const value = Number(properties[key]);
    const result = key === '_gpo_quantity_fixed' ? value : value * quantity;
    if (!Number.isSafeInteger(result) || result < 0) throw new Error('INVALID_FEE_RULE');
    return result;
  };
  const groups = cart => {
    const parents = new Map();
    for (const item of cart.items) {
      const group = item.properties?._gpo_product_group;
      if (!group) continue;
      if (parents.has(String(group))) throw new Error('AMBIGUOUS_GROUP');
      parents.set(String(group), item);
    }
    return parents;
  };
  const consistent = cart => {
    const parents = groups(cart);
    return cart.items.every(item => {
      const group = item.properties?._gpo_parent_product_group;
      if (!group) return true;
      const parent = parents.get(String(group));
      return !!parent && item.quantity === feeQuantity(item.properties, parent.quantity);
    });
  };
  const schedule = () => {
    clearTimeout(timer);
    if (failed || pending || dirty.size) return;
    timer = setTimeout(verify, 0);
  };
  const verify = async () => {
    if (failed || pending || dirty.size) return;
    const version = revision;
    try {
      if (window.filameltPropertyMigration) await window.filameltPropertyMigration;
      let cart = await read();
      if (cart.items.some(item => item.properties?._gpo_parent_product_group)) {
        const app = window.GPOConfigs;
        if (typeof app?.reconcileCart !== 'function') {
          if (Date.now() - started < 15000) { timer = setTimeout(verify, 100); return; }
          throw new Error('OPTIONS_NOT_READY');
        }
        if (app.cartReconcilePromise) await app.cartReconcilePromise;
        if (app.cartReconcileReloading || await app.reconcileCart(cart)) return;
        cart = await read();
      }
      if (version !== revision || pending || dirty.size) { schedule(); return; }
      if (document.getElementById('MainContent')?.inert || !consistent(cart)) throw new Error('CART_NOT_CONSISTENT');
      allowed = true;
      controls();
      show(false);
    } catch { failed = true; close(); show(true); }
  };
  window.fetch = function(input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== location.origin || method === 'GET' || !/\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?$/.test(url.pathname)) return nativeFetch(input, init);
    pending++; close(); show(false);
    return nativeFetch(input, init).then(response => {
      if (!response.ok) { failed = true; show(true); }
      return response;
    }).catch(error => { failed = true; show(true); throw error; }).finally(() => { pending--; schedule(); });
  };
  const requestChange = details => {
    const intent = dirty.get(details.key);
    if (intent === details.quantity) dirty.delete(details.key);
    pending++; close(); show(false);
    const operation = queue.then(async () => {
      const cart = await read();
      const parent = cart.items.find(item => item.key === details.key);
      if (!parent || !Number.isSafeInteger(details.quantity) || details.quantity < 0) throw new Error('CART_CHANGED');
      const group = parent.properties?._gpo_product_group;
      const payload = {sections:details.sections, sections_url:details.sections_url};
      const expected = new Map([[itemIdentity(parent), details.quantity]]);
      let endpoint = 'change';
      if (group) {
        groups(cart);
        payload.updates = {[parent.key]:details.quantity};
        cart.items.filter(item => String(item.properties?._gpo_parent_product_group) === String(group)).forEach(item => {
          payload.updates[item.key] = feeQuantity(item.properties, details.quantity);
          expected.set(itemIdentity(item), payload.updates[item.key]);
        });
        endpoint = 'update';
      } else { payload.id = parent.key; payload.quantity = details.quantity; }
      const response = await window.fetch(`${root}cart/${endpoint}.js`, {method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(payload)});
      const state = await response.json();
      if (!response.ok || state.errors || !Array.isArray(state.items)) throw new Error('CART_UPDATE_FAILED');
      for (const [identity, quantity] of expected) {
        const updated = state.items.find(item => itemIdentity(item) === identity);
        if (quantity ? updated?.quantity !== quantity : !!updated) throw new Error('QUANTITY_NOT_APPLIED');
      }
      if (!consistent(state)) throw new Error('FEE_QUANTITY_NOT_APPLIED');
      return state;
    });
    queue = operation.catch(() => {});
    return operation.catch(error => { failed = true; show(true); throw error; }).finally(() => { pending--; schedule(); });
  };
  window.filameltCart = {requestChange, feeQuantity, consistent, cancelInput(input) { dirty.delete(input?.dataset.quantityLineKey); schedule(); }};
  for (const type of ['input','change']) document.addEventListener(type, event => {
    const input = event.target.closest?.('cart-items input[name="updates[]"]');
    if (!input) return;
    dirty.set(input.dataset.quantityLineKey, Number(input.value));
    close(); show(false);
    if (type === 'input') input.dispatchEvent(new Event('change', {bubbles:true}));
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.id !== 'cart') return;
    if (!allowed || pending || dirty.size) { event.preventDefault(); event.stopImmediatePropagation(); controls(); show(failed); }
  }, true);
  new MutationObserver(controls).observe(document.documentElement, {childList:true,subtree:true});
  window.addEventListener('pageshow', () => { close(); schedule(); });
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('cart-items input[name="updates[]"]').forEach(input => {
      if (input.value === input.getAttribute('value')) return;
      dirty.set(input.dataset.quantityLineKey, Number(input.value));
      input.dispatchEvent(new Event('change', {bubbles:true}));
    });
    controls(); show(false); schedule();
  });
})();
